import { useCallback, useEffect, useRef } from "react";
import { useSocket } from "../../Context/SocketContext";

export const useSocketEvents = () => {
  const { socket, isConnected } = useSocket();
  // Every registered { eventName, handler } pair. Keyed by pair, not by event name:
  // a Map keyed by event name kept only the last handler per event, so the others
  // were never removed on unmount and kept firing (and leaking) after the view closed.
  const listenersRef = useRef(new Set());

  // Clean up all listeners on unmount
  useEffect(() => {
    const listeners = listenersRef.current;
    return () => {
      listeners.forEach(({ eventName, handler }) => {
        socket?.off(eventName, handler);
      });
      listeners.clear();
    };
  }, [socket]);

  const on = useCallback(
    (eventName, handler) => {
      if (!socket || !eventName || !handler) {
        console.warn("Invalid parameters for socket.on");
        return () => {};
      }

      // Create a wrapper that preserves the latest state
      const wrappedHandler = (...args) => {
        handler(...args);
      };

      // Store reference to the wrapper for cleanup
      const entry = { eventName, handler: wrappedHandler };
      listenersRef.current.add(entry);

      // Attach listener
      socket.on(eventName, wrappedHandler);

      // Return cleanup function
      return () => {
        socket.off(eventName, wrappedHandler);
        listenersRef.current.delete(entry);
      };
    },
    [socket]
  );

  const off = useCallback(
    (eventName, handler) => {
      if (!socket || !eventName) return;

      if (handler) {
        socket.off(eventName, handler);
      } else {
        // Remove all listeners for this event
        socket.off(eventName);
      }
      listenersRef.current.forEach((entry) => {
        if (entry.eventName === eventName && (!handler || entry.handler === handler)) {
          listenersRef.current.delete(entry);
        }
      });
    },
    [socket]
  );

  const emit = useCallback(
    (eventName, data, callback) => {
      if (!socket || !isConnected) {
        console.warn(`Cannot emit ${eventName}: Socket not connected`);
        return false;
      }

      return socket.emit(eventName, data, callback);
    },
    [socket, isConnected]
  );

  const once = useCallback(
    (eventName, handler) => {
      if (!socket || !eventName || !handler) {
        console.warn("Invalid parameters for socket.once");
        return () => {};
      }

      const entry = { eventName, handler: null };
      const wrappedHandler = (...args) => {
        handler(...args);
        listenersRef.current.delete(entry);
      };
      entry.handler = wrappedHandler;

      listenersRef.current.add(entry);
      socket.socket?.once(eventName, wrappedHandler);

      return () => {
        socket.off(eventName, wrappedHandler);
        listenersRef.current.delete(entry);
      };
    },
    [socket]
  );

  return {
    on,
    off,
    emit,
    once,
    isConnected,
    socketId: socket?.getSocketId(),
  };
};
